import styled from '@emotion/styled';
import React, { useEffect, useRef } from 'react';
import { Box } from '../../../styles';
import { IconArrowRight } from '../../../landing/icons';
import { compactChrome, PanelItemIconLabel, PanelMultiSelectItemWrapper } from './index';

interface Position {
  left?: string;
  right?: string;
}

const Panel = ({ show, title, children, horizontalPos, onClose, customPadding }:
                 {
                   title?: string,
                   children: JSX.Element,
                   onClose: () => void,
                   show?: boolean,
                   horizontalPos?: Position,
                   customPadding?: {default: string, responsive: string}
                 }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // if (!show) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (show && panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <PanelWrapper ref={panelRef} horizontalPos={horizontalPos} show={show} customPadding={customPadding}>
      {title && (
        <>
          <Header>{title}</Header>
          <Divider />
        </>
      )}
      {children}
    </PanelWrapper>
  );
};

export const PanelWrapper = styled(Box)<{ horizontalPos?: Position, show?: boolean, customPadding?: {default: string, responsive: string}}>(({ theme, show = false, horizontalPos, customPadding }) => {
  return {
    display: show ? 'block' : 'none',
    position: 'absolute',
    bottom: '6rem',
    ...horizontalPos,
    background: theme.palette.background[800][0],
    borderRadius: '0.5rem',
    transition: 'opacity 0.3s ease-in-out 0s, transform 0.3s ease-in-out 0s',
    zIndex: 20,
    padding: !customPadding?.default ? '1rem' : customPadding.default,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-webkit-scrollbar-thumb': {
      display: 'none',
    },
    ...compactChrome({
      bottom: '5rem',
      padding: !customPadding?.responsive ? '0.5rem 1rem' : customPadding.responsive,
      '& svg': {
        width: '0.8rem',
      },
      '& span': {
        fontSize: '0.75rem',
      },
    }),
  };
});

export const Header = styled.div(({ theme }) => {
  return {
    textAlign: 'center',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 500,
    color: theme.palette.background[800][0],
    lineHeight: '2.68vw',

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem',
    },
  };
});

export const Divider = styled.div(({ theme }) => {
  return {
    height: '0.1vw',
    width: 'calc(100% - 2rem)',
    backgroundColor: theme.palette.foreground[100][0],
    margin: 'auto',
  };
});

export const PanelSettingLayer = styled.div(() => {
  return {
    height: '100%',
    zIndex: 21,
  };
});

export const PanelSettingHeader = ({ label, handleClick }) => {
  return (
    <>
      <PanelMultiSelectItemWrapper onClick={handleClick} cursor={'pointer'} notHover>
        <PanelItemIconLabel icon={<IconArrowRight />} label={label} />
      </PanelMultiSelectItemWrapper>
      <Divider />
    </>
  );
};

export default Panel;
